import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { createExceptionRule, getPaycodesDurationType } from 'utils/ExceptionRule';
import { normalizeString } from 'utils/Validations';
import {
    ExceptionRuleFormDialog,
    type ExceptionRuleFormValues,
    type PaycodeOption,
} from './ExceptionRuleFormDialog';

type RowItem = { id: number | string; name: string };

type AddExceptionRuleProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    reloading: () => void;
    rows: RowItem[];
};

const defaultValues: ExceptionRuleFormValues = {
    name: '',
    description: '',
    not_planning: false,
    long_interval: '99:00',
    shortened_shift: '00:00',
    in_punch_very_early: '24:00',
    in_punch_early: '24:00',
    in_punch_late: '24:00',
    out_punch_early: '24:00',
    out_punch_late: '24:00',
    out_punch_very_late: '24:00',
};

const AddExceptionRule = ({ open, setOpen, reloading, rows }: AddExceptionRuleProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(false);
    const [values, setValues] = useState<ExceptionRuleFormValues>(defaultValues);
    const [paycodes, setPaycodes] = useState<any[]>([]);
    const [inPaycodesDuration, setInPaycodesDuration] = useState<any | null>(null);
    const [outPaycodesDuration, setOutPaycodesDuration] = useState<any | null>(null);

    useEffect(() => {
        if (!open) return;

        getPaycodesDurationType(setLoading, 'exception_rule')
            .then((response) => {
                setPaycodes(response || []);
            })
            .catch((error: Error) => {
                console.error(error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [open]);

    const cleanForm = () => {
        setValues(defaultValues);
        setInPaycodesDuration(null);
        setOutPaycodesDuration(null);
        setPaycodes([]);
        setLoading(false);
        setOpen(false);
        setAlert(false);
    };

    const existName = (name: string) => {
        const normalizedInput = normalizeString(name);
        return rows.some((row) => normalizeString(row.name) === normalizedInput);
    };

    const validateInPunchVeryEarly = (value: string) => {
        if (value.length === 5 && values.in_punch_very_early) {
            const inPunchEarly = value.split(':');
            const inPunchVeryEarly = values.in_punch_very_early.split(':');

            const inPunchEarlyHours = parseInt(inPunchEarly[0], 10);
            const inPunchEarlyMinutes = parseInt(inPunchEarly[1], 10);
            const inPunchVeryEarlyHours = parseInt(inPunchVeryEarly[0], 10);
            const inPunchVeryEarlyMinutes = parseInt(inPunchVeryEarly[1], 10);

            if (inPunchVeryEarlyHours > inPunchEarlyHours) return false;
            if (inPunchVeryEarlyHours === 24 && inPunchEarlyHours === 24) return false;
            if (inPunchEarlyHours === 24 && inPunchEarlyMinutes === 0) return false;
            if (inPunchVeryEarlyHours === inPunchEarlyHours && inPunchVeryEarlyMinutes > inPunchEarlyMinutes) return false;

            return true;
        }

        return false;
    };

    const validateOutPunchVeryLate = (value: string) => {
        if (value.length === 5 && values.out_punch_very_late) {
            const outPunchLate = value.split(':');
            const outPunchVeryLate = values.out_punch_very_late.split(':');

            const outPunchLateHours = parseInt(outPunchLate[0], 10);
            const outPunchLateMinutes = parseInt(outPunchLate[1], 10);
            const outPunchVeryLateHours = parseInt(outPunchVeryLate[0], 10);
            const outPunchVeryLateMinutes = parseInt(outPunchVeryLate[1], 10);

            if (outPunchVeryLateHours > outPunchLateHours) return false;
            if (outPunchVeryLateHours === 24 && outPunchLateHours === 24) return false;
            if (outPunchLateHours === 24 && outPunchLateMinutes === 0) return false;
            if (outPunchVeryLateHours === outPunchLateHours && outPunchVeryLateMinutes > outPunchLateMinutes) return false;

            return true;
        }

        return false;
    };

    const timeValidation = (value: string) => value.length > 0 && value.length < 5;

    const hasInvalidTimeFormat = [
        values.long_interval,
        values.shortened_shift,
        values.in_punch_very_early,
        values.in_punch_early,
        values.in_punch_late,
        values.out_punch_early,
        values.out_punch_late,
        values.out_punch_very_late,
    ].some((timeValue) => timeValidation(timeValue));

    const submitDisabled = useMemo(
        () =>
            values.name === '' ||
            hasInvalidTimeFormat ||
            validateInPunchVeryEarly(values.in_punch_early) ||
            validateOutPunchVeryLate(values.out_punch_late) ||
            existName(values.name),
        [values, hasInvalidTimeFormat],
    );

    const changes = () => {
        const hasChanges =
            values.name !== '' ||
            values.description !== '' ||
            values.not_planning !== defaultValues.not_planning ||
            values.long_interval !== defaultValues.long_interval ||
            values.shortened_shift !== defaultValues.shortened_shift ||
            values.in_punch_very_early !== defaultValues.in_punch_very_early ||
            values.in_punch_early !== defaultValues.in_punch_early ||
            values.in_punch_late !== defaultValues.in_punch_late ||
            values.out_punch_early !== defaultValues.out_punch_early ||
            values.out_punch_late !== defaultValues.out_punch_late ||
            values.out_punch_very_late !== defaultValues.out_punch_very_late ||
            inPaycodesDuration !== null ||
            outPaycodesDuration !== null;

        if (hasChanges) {
            setAlert(true);
            return;
        }

        cleanForm();
    };

    const addExceptionRule = async () => {
        const body = {
            name: values.name,
            not_planning: values.not_planning,
            long_interval: values.long_interval,
            shortened_shift: values.shortened_shift,
            in_punch_very_early: values.in_punch_very_early,
            in_punch_early: values.in_punch_early,
            in_punch_late: values.in_punch_late,
            out_punch_early: values.out_punch_early,
            out_punch_late: values.out_punch_late,
            out_punch_very_late: values.out_punch_very_late,
            in_paycode_id: inPaycodesDuration?.paycode_id || null,
            out_paycode_id: outPaycodesDuration?.paycode_id || null,
            description: values.description,
            enable: true,
        };

        createExceptionRule(setLoading, body)
            .then(() => {
                enqueueSnackbar(t('settings.exception_rules.table.dialog.add.messages.success'), { variant: 'success' });
                setOpen(false);
                cleanForm();
                reloading();
            })
            .catch((error: Error) => {
                console.error('Error:', error);
                enqueueSnackbar(t('settings.exception_rules.table.dialog.add.messages.error'), { variant: 'error' });
                setOpen(false);
            });
    };

    return (
        <ExceptionRuleFormDialog
            open={open}
            mode="add"
            loading={loading}
            values={values}
            paycodeOptions={paycodes as PaycodeOption[]}
            inPaycodesDuration={inPaycodesDuration}
            outPaycodesDuration={outPaycodesDuration}
            nameExists={existName(values.name)}
            nameExistsTextKey="custom_reports.category.table.dialog.add.exist_name"
            validateInPunchVeryEarly={validateInPunchVeryEarly}
            validateOutPunchVeryLate={validateOutPunchVeryLate}
            timeValidation={timeValidation}
            submitDisabled={submitDisabled}
            showUnsavedAlert={alert}
            onNameChange={(name) => setValues((prev) => ({ ...prev, name }))}
            onDescriptionChange={(description) => setValues((prev) => ({ ...prev, description }))}
            onNotPlanningChange={(checked) => setValues((prev) => ({ ...prev, not_planning: checked }))}
            onTimeFieldChange={(field, value) => setValues((prev) => ({ ...prev, [field]: value }))}
            onInPaycodeChange={setInPaycodesDuration}
            onOutPaycodeChange={setOutPaycodesDuration}
            onRequestClose={changes}
            onDismissUnsavedAlert={() => setAlert(false)}
            onDiscardChanges={cleanForm}
            onSubmit={addExceptionRule}
        />
    );
};

export { AddExceptionRule };
